import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Dialog, Button, Textarea } from './ui';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, feedback: string) => Promise<void>;
}

const DELETION_REASONS = [
  { value: 'not_using', label: 'サービスを使わなくなった' },
  { value: 'too_expensive', label: '料金が高い' },
  { value: 'not_satisfied', label: 'サービス内容に満足できなかった' },
  { value: 'found_alternative', label: '他のサービスを使うことにした' },
  { value: 'temporary', label: '一時的に利用を停止したい' },
  { value: 'privacy', label: 'プライバシーの懸念' },
  { value: 'other', label: 'その他' },
];

export default function DeleteAccountModal({ isOpen, onClose, onConfirm }: DeleteAccountModalProps) {
  const [step, setStep] = useState<'survey' | 'confirm'>('survey');
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleNext = () => {
    if (!reason) {
      setError('削除理由を選択してください');
      return;
    }
    setError('');
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await onConfirm(reason, feedback);
    } catch (err) {
      setError('アカウントの削除に失敗しました');
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      setStep('survey');
      setReason('');
      setFeedback('');
      setError('');
      onClose();
    }
  };

  return (
    // Dialog（005）が focus trap / focus return / Escape / scroll lock / aria-modal を担保。
    // 可視ヘッダー（タイトル + X）を自前で持つため ariaLabel で accessible name を与える。
    <Dialog open={isOpen} onClose={handleClose} ariaLabel="アカウント削除" size="md">
      {/* ヘッダー（Dialog の px-6 py-4 コンテンツ領域内。下罫で本文と区切る） */}
      <div className="flex items-center justify-between pb-3 mb-3 border-b">
        <h2 className="text-xl font-bold text-text">アカウント削除</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          disabled={deleting}
          aria-label="閉じる"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {step === 'survey' ? (
        /* アンケートステップ。選択肢が7件あるため余白は詰めて全体を縦に収める。
           Dialog の max-h-[90vh] に収まればスクロールバーは出ず、画面が縦に短い端末でのみ自動で出る。 */
        <div>
          <p className="text-gray-600 mb-3">
            サービス改善のため、削除理由をお聞かせください。
          </p>

          {error && (
            <div role="alert" className="bg-danger-bg text-danger-fg px-3 py-2 rounded-field mb-3 text-sm">
              {error}
            </div>
          )}

          <fieldset className="mb-4">
            <legend className="sr-only">削除理由</legend>
            <div className="space-y-1.5">
              {DELETION_REASONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                    reason === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={option.value}
                    checked={reason === option.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="mr-3 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="mb-4">
            <label htmlFor="delete-feedback" className="block text-sm font-medium text-gray-700 mb-1.5">
              ご意見・ご要望（任意）
            </label>
            <Textarea
              id="delete-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="改善点やご要望があればお聞かせください..."
              className="resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} className="flex-1">
              キャンセル
            </Button>
            <Button variant="danger" onClick={handleNext} className="flex-1">
              次へ
            </Button>
          </div>
        </div>
      ) : (
        /* 確認ステップ */
        <div>
          <div className="flex items-center gap-3 p-4 bg-danger-bg rounded-lg mb-4">
            <AlertTriangle className="w-8 h-8 text-danger flex-shrink-0" />
            <div>
              <p className="font-bold text-danger-fg">本当に削除しますか？</p>
              <p className="text-sm text-danger-fg">この操作は取り消せません</p>
            </div>
          </div>

          <p className="text-gray-700 mb-4">
            以下のデータがすべて完全に削除されます：
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
            <li>プロフィール情報</li>
            <li>すべての書き起こしデータ</li>
            <li>プラン・課金情報</li>
          </ul>

          {error && (
            <div role="alert" className="bg-danger-bg text-danger-fg px-3 py-2 rounded-field mb-4 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setStep('survey')}
              disabled={deleting}
              className="flex-1"
            >
              戻る
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              loading={deleting}
              disabled={deleting}
              className="flex-1"
            >
              {deleting ? '削除中...' : '削除する'}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
